// A2-582 — one row's answers, exactly as the server sent them (row.answers;
// see answers.js). Each kind is drawn by the control that already existed
// for it; which controls appear is the server's decision, never this file's.
import React from "react";
import { useTranslation } from "react-i18next";
import { PerRowResolver } from "./PerRowResolver";
import { VatNumberAmend } from "./VatNumberAmend";
import { FxTreatmentAmend } from "./FxTreatmentAmend";
import { Art59CategoryResolver } from "./Art59CategoryResolver";
import { CustomerEstablishmentResolver } from "./CustomerEstablishmentResolver";
import { PropertyLettingResolver } from "./PropertyLettingResolver";
import { RefundChannelResolver } from "./RefundChannelResolver";
import { LocationCountryPicker } from "./LocationCountryPicker";
import { BusinessEvidence, EvidenceWithdraw } from "./BusinessEvidence";
import { LeaveOutCopy } from "./LeaveOutCopy";
import { CustomerCountry, SoldAs } from "./CustomerAnswers";
import { ClearDecision } from "./ClearDecision";
import { GoToAnswer, FixInFileAnswer, NoWayOutAnswer } from "./AnswerNotes";
import { BulkTick } from "./bulkDecision";
import { rowAnswersOnCard, TICK_KINDS } from "./answers";

export const RowAnswers = ({
  row, g, tid, calcId, clientId, calcFrozen, rowAcked, onInspect, onDone, data, bulkSel, setBulkSel,
}) => {
  const { t } = useTranslation();
  const answers = rowAnswersOnCard(row, g);
  const live = !rowAcked && !calcFrozen;
  // A tick box when this row takes an answer given for ticked rows (the rate
  // pick above the rows included, which the row itself does not repeat).
  const tickable = live && (row?.answers || []).some((a) => TICK_KINDS.has(a.kind)
    && a.kind !== "PROPERTY_LETTING"   // its own control draws its tick (A2-484)
    && (a.kind !== "KEEP" || (g.family === "B" && (a.flags || []).includes(g.flag_type))));
  const draw = (a, i) => {
    const k = `${a.kind}-${i}`;
    switch (a.kind) {
      case "KEEP":
        // Family A rows are marked reviewed together (the card's composer),
        // except beside a number fix: on "VAT check unavailable" keeping a
        // row zero-rates it, so it is decided one checked row at a time.
        return g.family === "B" || answers.some((x) => x.kind === "VAT_NUMBER") ? (
          <PerRowResolver key={k} hideChip tid={tid} flagType={g.flag_type} calcId={calcId}
            calcFrozen={calcFrozen} acked={rowAcked} onResolved={onDone}
            groupTotal={g.count} groupRemaining={(g.count || 0) - (g.acknowledged_count || 0)} />
        ) : null;
      case "VAT_NUMBER":
        return live ? <VatNumberAmend key={k} tid={tid} calcId={calcId} onResolved={onDone} /> : null;
      case "BUSINESS_EVIDENCE":   // A2-592
        return live ? <BusinessEvidence key={k} tid={tid} calcId={calcId} calcFrozen={calcFrozen}
          invoiceDate={row?.transaction_date || row?.date} onResolved={onDone} /> : null;
      case "EVIDENCE_WITHDRAW":   // A2-592 — also once the note is marked reviewed
        return !calcFrozen ? <EvidenceWithdraw key={k} tid={tid} calcId={calcId} calcFrozen={calcFrozen}
          onResolved={onDone} /> : null;
      case "FX_TREATMENT":
        return live ? <FxTreatmentAmend key={k} tid={tid} calcId={calcId} onResolved={onDone}
          reinvoiced={(g.reinvoiced_fx_txn_ids || []).includes(tid)} /> : null;
      case "PICK_COUNTRY":
        return <LocationCountryPicker key={k} tids={[tid]} field={a.field} calcId={calcId}
          calcFrozen={calcFrozen || rowAcked} onResolved={onDone} />;
      case "SERVICE_CATEGORY":
        return <Art59CategoryResolver key={k} hideChip tid={tid} calcId={calcId}
          calcFrozen={calcFrozen} acked={rowAcked} onResolved={onDone} />;
      case "ESTABLISHMENT":
        return <CustomerEstablishmentResolver key={k} hideChip tids={[tid]} calcId={calcId}
          calcFrozen={calcFrozen} acked={rowAcked} buyerVat={row?.buyer_vat_number}
          saved={row?.established} onResolved={onDone} />;
      case "PROPERTY_LETTING":
        return <PropertyLettingResolver key={k} hideChip tids={[tid]} calcId={calcId}
          calcFrozen={calcFrozen} acked={rowAcked && !row?.letting} current={row?.letting?.answer}
          country={row?.letting?.country || row?.rc_rule?.country} onResolved={onDone}
          sel={row?.letting ? undefined : bulkSel} setSel={row?.letting ? undefined : setBulkSel} />;
      case "CREDIT_NOTE_CHANNEL":
        return <RefundChannelResolver key={k} hideChip tid={tid} calcId={calcId}
          calcFrozen={calcFrozen} acked={rowAcked} options={a.channels || []}
          active={data?.refund_channel_active || []} onResolved={onDone} />;
      case "LEAVE_OUT_COPY":   // A1-103 step 4
        return live && !row?.left_out ? <LeaveOutCopy key={k} tids={[tid]} calcId={calcId}
          calcFrozen={calcFrozen} onResolved={onDone} /> : null;
      case "CUSTOMER_COUNTRY":   // A1-104
        return live || row?.customer_country ? <CustomerCountry key={k} tids={[tid]} calcId={calcId}
          calcFrozen={calcFrozen || rowAcked} numberCountry={row?.number_country}
          current={row?.customer_country} onResolved={onDone} /> : null;
      case "SOLD_AS":   // A1-104
        return live || row?.sold_as ? <SoldAs key={k} tids={[tid]} calcId={calcId}
          calcFrozen={calcFrozen || rowAcked} current={row?.sold_as} onResolved={onDone} /> : null;
      case "CLEAR_DECISION":   // A2-586
        return live ? <ClearDecision key={k} tids={[tid]} calcId={calcId} calcFrozen={calcFrozen}
          onResolved={onDone} /> : null;
      case "GO_TO":
        return <GoToAnswer key={k} answer={a} clientId={clientId} />;
      case "FIX_IN_FILE":
        return <FixInFileAnswer key={k} answer={a} />;
      case "NO_WAY_OUT":
        return <NoWayOutAnswer key={k} />;
      default:   // PICK_RATE, ONLINE_EVENT_CHOICE, ADD_REGISTRATION: the group's control
        return null;
    }
  };
  return (
    <>
      {tickable && <BulkTick tid={tid} sel={bulkSel} setSel={setBulkSel} />}
      {rowAcked && g.family === "B" && !answers.some((a) => a.kind === "KEEP") && (
        <span data-testid={`rg-done-${tid}`} data-resolved="true" className="text-xs text-emerald-700 dark:text-emerald-300">
          ✓ {t("ui.review_grouped.row_resolved_label", { defaultValue: "Reviewed" })}
        </span>
      )}
      {row?.left_out && (
        <span data-testid={`rg-left-out-${tid}`} className="text-xs text-emerald-700 dark:text-emerald-300">
          ✓ {t("ui.review_answers.left_out_note", { defaultValue: "Left out — applied at the next re-run" })}
        </span>
      )}
      {row?.cleared && (
        <span data-testid={`rg-cleared-${tid}`} className="text-xs text-emerald-700 dark:text-emerald-300">
          ✓ {t("ui.review_answers.cleared_note", { defaultValue: "Earlier answer cleared — applied at the next re-run" })}
        </span>
      )}
      {answers.map(draw)}
      <button type="button" data-testid={`rg-txn-${tid}`} disabled={!onInspect}
              onClick={() => onInspect?.({ transaction_ids: [tid], txn_count: 1 })}
              title={t("ui.review_grouped.inspect_row_tooltip", { defaultValue: "Inspect source transaction" })}
              className="text-xs px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800">
        {t("ui.review_grouped.inspect_row", { defaultValue: "Inspect" })}
      </button>
    </>
  );
};

export default RowAnswers;
